import { FontFamily } from '@euler/components/typography/fonts';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useMemo } from 'react';
import { StyleSheet } from 'react-native';

export const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        dimmer: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
        panel: {
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          borderTopLeftRadius: rpx(20),
          borderTopRightRadius: rpx(20),
          shadowColor: '#000',
          shadowOffset: { width: 0, height: -4 },
          shadowOpacity: 0.12,
          shadowRadius: 20,
          elevation: 20,
        },
        desktopPanel: {
          position: 'absolute',
          backgroundColor: '#fff',
          borderRadius: 16,
          borderWidth: 1,
          borderColor: 'rgba(0, 0, 0, 0.08)',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.25,
          shadowRadius: 40,
          elevation: 32,
          overflow: 'hidden',
        },
        dragHandleContainer: {
          alignItems: 'center',
          paddingTop: rpx(10),
          paddingBottom: rpx(2),
        },
        dragHandle: {
          width: rpx(36),
          height: rpx(4),
          borderRadius: rpx(2),
          backgroundColor: '#D1D5DB',
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: rpx(16),
          paddingTop: rpx(6),
          paddingBottom: rpx(12),
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: '#E5E7EB',
        },
        headerGradient: {
          borderBottomWidth: 0,
          paddingTop: rpx(12),
          paddingBottom: rpx(12),
        },
        headerGradientBg: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        },
        headerLeft: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(8),
        },
        headerTitle: {
          fontSize: rpx(16),
          fontFamily: FontFamily.NotoSans.Bold,
          color: '#1E293B',
        },
        headerStreamingBadge: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(4),
          paddingHorizontal: rpx(8),
          paddingVertical: rpx(2),
          borderRadius: rpx(10),
          backgroundColor: '#EFF6FF',
        },
        headerStreamingText: {
          fontSize: rpx(10),
          fontFamily: FontFamily.NotoSans.Medium,
        },
        headerRight: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(12),
        },
        headerBtn: {
          padding: rpx(4),
        },
        creditNotice: {
          paddingHorizontal: rpx(16),
          paddingVertical: rpx(8),
        },
        emptyState: {
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal: rpx(32),
          // Centered content overflows BOTH ends once it outgrows the box (the
          // keyboard shrinks it), and iOS doesn't clip by default — without
          // this the last suggestions paint over the input bar.
          overflow: 'hidden',
        },
        emptyIcon: {
          width: rpx(64),
          height: rpx(64),
          borderRadius: rpx(32),
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: rpx(16),
        },
        emptyTitle: {
          fontSize: rpx(18),
          fontFamily: FontFamily.NotoSans.Bold,
          color: '#1E293B',
          marginBottom: rpx(8),
        },
        emptyDesc: {
          fontSize: rpx(14),
          fontFamily: FontFamily.NotoSans.Regular,
          color: '#64748B',
          textAlign: 'center',
          lineHeight: rpx(22),
          marginBottom: rpx(28),
        },
        suggestionsContainer: {
          width: '100%',
          // RN defaults flexShrink to 0: without this the list keeps its full
          // height when the box is smaller and spills past the bottom.
          flexShrink: 1,
        },
        skeletonRow: {
          marginBottom: rpx(12),
        },
        suggestionBtn: {
          borderWidth: 1,
          borderRadius: rpx(12),
          paddingHorizontal: rpx(16),
          paddingVertical: rpx(12),
          marginBottom: rpx(8),
        },
        suggestionBtnText: {
          fontSize: rpx(14),
          fontFamily: FontFamily.NotoSans.Regular,
          lineHeight: rpx(20),
        },
        messageListContent: {
          paddingVertical: rpx(12),
          paddingLeft: rpx(12),
          paddingRight: rpx(16),
        },
        scrollToBottomBtn: {
          position: 'absolute',
          right: rpx(16),
          bottom: rpx(8),
          width: rpx(32),
          height: rpx(32),
          borderRadius: rpx(16),
          backgroundColor: '#fff',
          alignItems: 'center',
          justifyContent: 'center',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 4,
          elevation: 4,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: '#E2E8F0',
        },
        bubbleRow: {
          flexDirection: 'row',
          marginBottom: rpx(16),
          alignItems: 'flex-start',
        },
        bubbleRowUser: {
          justifyContent: 'flex-end',
        },
        bubbleRowAssistant: {
          justifyContent: 'flex-start',
        },
        avatar: {
          width: rpx(28),
          height: rpx(28),
          borderRadius: rpx(14),
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: rpx(8),
          marginTop: rpx(2),
        },
        bubbleUserOuter: {
          flexShrink: 1,
          maxWidth: '80%',
        },
        bubbleAssistantOuter: {
          flex: 1,
        },
        bubble: {
          borderRadius: rpx(16),
          paddingHorizontal: rpx(14),
          paddingVertical: rpx(10),
        },
        bubbleUser: {
          borderTopRightRadius: rpx(4),
          alignSelf: 'flex-end',
        },
        bubbleAssistant: {
          backgroundColor: '#F1F5F9',
          borderTopLeftRadius: rpx(4),
        },
        bubbleTextUser: {
          fontSize: rpx(14),
          lineHeight: rpx(20),
          fontFamily: FontFamily.NotoSans.Regular,
          color: '#fff',
        },
        bubbleError: {
          backgroundColor: '#FEF2F2',
          borderLeftWidth: 2,
          borderLeftColor: '#EF4444',
        },
        bubbleMeta: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(8),
          marginTop: rpx(3),
          paddingHorizontal: rpx(2),
        },
        bubbleMetaUser: {
          justifyContent: 'flex-end',
        },
        bubbleMetaAssistant: {
          justifyContent: 'flex-start',
        },
        timestamp: {
          fontSize: rpx(10),
          fontFamily: FontFamily.NotoSans.Regular,
          color: '#94A3B8',
        },
        reactionRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(2),
        },
        reactionBtn: {
          padding: rpx(4),
          borderRadius: rpx(10),
        },
        reactionBtnActive: {
          backgroundColor: '#F1F5F9',
        },
        retryButton: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(3),
          paddingVertical: rpx(2),
        },
        retryText: {
          fontSize: rpx(12),
          fontFamily: FontFamily.NotoSans.Medium,
          color: '#EF4444',
        },
        suggestionBar: {
          paddingHorizontal: 12,
          paddingTop: 10,
          paddingBottom: 4,
          gap: 6,
        },
        suggestionBarChip: {
          borderWidth: StyleSheet.hairlineWidth,
          borderRadius: 14,
          paddingHorizontal: 12,
          paddingVertical: 6,
          backgroundColor: '#F1F5F9',
        },
        suggestionBarText: {
          fontSize: 12,
          fontFamily: FontFamily.NotoSans.Regular,
        },
        inputContainer: {
          flexDirection: 'row',
          alignItems: 'flex-end',
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(10),
          gap: rpx(10),
        },
        inputFieldWrapper: {
          flex: 1,
          backgroundColor: '#F1F5F9',
          borderRadius: rpx(22),
          borderWidth: 1,
          borderColor: '#E2E8F0',
        },
        inputFieldDisabled: {
          opacity: 0.6,
        },
        inputField: {
          fontSize: rpx(15),
          fontFamily: FontFamily.NotoSans.Regular,
          color: '#1E293B',
          paddingHorizontal: rpx(18),
          paddingTop: rpx(12),
          paddingBottom: rpx(12),
          textAlignVertical: 'center',
        },
        actionBtn: {
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: rpx(2),
        },
        errorSuggestions: {
          paddingHorizontal: rpx(52),
          paddingVertical: rpx(6),
          gap: rpx(6),
        },
        errorSuggestionChip: {
          borderWidth: 1,
          borderRadius: rpx(12),
          paddingHorizontal: rpx(12),
          paddingVertical: rpx(6),
        },
        errorSuggestionText: {
          fontSize: rpx(12),
          fontFamily: FontFamily.NotoSans.Regular,
        },
      }),
    [rpx],
  );
};
